import { useAuth } from "../../utility/useAuth";
import {
  DownloadButton,
  DownloadContent,
  DownloadHeader,
  EmptyState,
  Eyebrow,
  FileDescription,
  FileIcon,
  FileInfo,
  FileList,
  FileName,
  FileRow,
  PageDescription,
  PageTitle,
  ReleaseCard,
  ReleaseCount,
  ReleaseHeader,
  ReleaseList,
  ReleaseType,
  ReleaseVersion,
  StyledDownloadPage,
} from "./DownloadPage.styles";
import { useEffect, useRef, useState } from "react";
import { PathItem } from "../../types/PathItem";
import { ToastUtility } from "@syncfusion/ej2-react-notifications";
import {
  getDocumentContent,
  getDocuments,
  getDownload,
} from "../../services/ApiService";
import {
  getFile,
  getFileExtension,
  getFolder,
  getPathItem,
} from "../../utility/pathHelper";
import { DownloadItem } from "../../types/DownloadItem";
import { ProgressButtonComponent } from "@syncfusion/ej2-react-splitbuttons";

type DownloadActionProps = {
  fileName: string;
  description: string;
  onDownload: () => Promise<Blob | undefined>;
  errorMessage: string;
};

const DownloadAction: React.FC<DownloadActionProps> = ({
  fileName,
  description,
  onDownload,
  errorMessage,
}) => {
  const progressButton = useRef<ProgressButtonComponent | null>(null);

  const downloadFile = async () => {
    try {
      const data = await onDownload();
      if (!data) return;

      const href = URL.createObjectURL(data);
      const link = document.createElement("a");
      link.href = href;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(href);
    } catch (error) {
      const message = `${errorMessage}: ${error}`;
      console.error(message);
      ToastUtility.show(message, "Error", 2000);
    } finally {
      progressButton.current?.stop();
      progressButton.current?.progressComplete();
    }
  };

  return (
    <FileRow>
      <FileIcon aria-hidden="true">
        <span className="e-icons e-download" />
      </FileIcon>
      <FileInfo>
        <FileName title={fileName}>{fileName}</FileName>
        <FileDescription>{description}</FileDescription>
      </FileInfo>
      <DownloadButton>
        <ProgressButtonComponent
          ref={progressButton}
          iconCss="e-icons e-download"
          enableProgress={true}
          duration={60000}
          onClick={downloadFile}
        >
          Download
        </ProgressButtonComponent>
      </DownloadButton>
    </FileRow>
  );
};

export const DownloadPage: React.FC = () => {
  const auth = useAuth();

  const [downloads, setDownloads] = useState<DownloadItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const downloadFolder: string = "csm-manager";

  useEffect(() => {
    const fetchData = async () => {
      try {
        const documents = await getDocuments(
          auth,
          undefined,
          downloadFolder,
          true,
        );
        const documentsToDownload = documents.map((document) =>
          getPathItem(document),
        );

        const items: DownloadItem[] = [];
        let item: DownloadItem = {} as DownloadItem;
        let files: PathItem[] = [];

        documentsToDownload.forEach((element) => {
          const version = getFolder(element.fileName);
          if (item.version !== version) {
            files = [element];
            item = { version, files };
            items.push(item);
          } else {
            files.push(element);
          }
        });

        setDownloads(items.sort((a, b) => b.version.localeCompare(a.version)));
      } catch (error) {
        const message = `Failed to get downloads. Error: ${error}`;
        console.error(message);
        ToastUtility.show(message, "Error", 2000);
      } finally {
        setIsLoading(false);
      }
    };
    void fetchData();
  }, []);

  return (
    <StyledDownloadPage>
      <DownloadContent>
        <DownloadHeader>
          <Eyebrow>Software downloads</Eyebrow>
          <PageTitle>CSM.Manager</PageTitle>
          <PageDescription>
            Download the latest release or choose an earlier version. Select a file below to start your download.
          </PageDescription>
          {!isLoading && (
            <ReleaseCount>
              <span className="e-icons e-download" aria-hidden="true" />
              {downloads.length} {downloads.length === 1 ? "release" : "releases"} available
            </ReleaseCount>
          )}
        </DownloadHeader>

        {isLoading ? (
          <EmptyState role="status">Loading available releases...</EmptyState>
        ) : downloads.length === 0 ? (
          <EmptyState>No releases are available right now.</EmptyState>
        ) : (
          <ReleaseList>
            {downloads.map((release) => {
              const isZipRelease =
                getFileExtension(release.files[0].fileName) === "zip";

              return (
                <ReleaseCard key={release.version}>
                  <ReleaseHeader>
                    <ReleaseVersion>Version {release.version}</ReleaseVersion>
                    <ReleaseType>
                      {isZipRelease ? "Files" : "Installer"}
                    </ReleaseType>
                  </ReleaseHeader>
                  <FileList>
                    {isZipRelease ? (
                      release.files.map((file) => (
                        <DownloadAction
                          key={file.fileName}
                          fileName={getFile(file.fileName)}
                          description="ZIP archive"
                          errorMessage="Failed to get download file"
                          onDownload={() =>
                            getDocumentContent(
                              auth,
                              downloadFolder,
                              file.fileName.replace("/", "--"),
                            )
                          }
                        />
                      ))
                    ) : (
                      <DownloadAction
                        fileName={`CSM.Manager.V${release.version}.zip`}
                        description="CSM.Manager installer"
                        errorMessage="Failed to get download"
                        onDownload={() => getDownload(auth, release.version)}
                      />
                    )}
                  </FileList>
                </ReleaseCard>
              );
            })}
          </ReleaseList>
        )}
      </DownloadContent>
    </StyledDownloadPage>
  );
};
